import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { api } from "@/lib/api";
import { Panel } from "@/components/ui/Panel";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { CopyButton, MonoText } from "@/components/ui/CopyButton";
import { EmptyState, LoadingState, ErrorState } from "@/components/ui/States";
import { formatDate } from "@/lib/format";
import type { UserVaultResponse, VaultEntitlement } from "@ag5r-store/shared";

type Tab = "active" | "lifetime" | "expired" | "wishlist";

/**
 * Kunci datanya tetap "lifetime" karena itu bentuk respons API, tapi yang
 * dilihat pembeli adalah "Sekali Bayar" — janji yang bisa kita tepati.
 */
const TAB_LABELS: Record<Tab, string> = {
  active: "Aktif",
  lifetime: "Sekali Bayar",
  expired: "Kedaluwarsa",
  wishlist: "Wishlist",
};

const TAB_ICONS: Record<Tab, string> = {
  active: "fa-circle-check",
  lifetime: "fa-infinity",
  expired: "fa-clock-rotate-left",
  wishlist: "fa-heart",
};

/**
 * Kartu akun: identitas pembeli + setelan nama.
 *
 * Nama default diambil dari bagian sebelum "@" pada email, bukan dari Google —
 * baris user dibuat di Worker dari klaim token yang belum tentu memuat `name`.
 * Pembeli bisa menimpanya kapan saja.
 */
function AccountCard({ email, role }: { email: string; role: string }) {
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [joined, setJoined] = useState<string | null>(null);

  useEffect(() => {
    api.getProfile()
      .then((p) => {
        setName(p.display_name ?? "");
        setJoined(p.created_at);
      })
      .catch(() => {
        /* Kartu tetap tampil dengan data dari sesi. */
      });
  }, []);

  const shown = name.trim() || email.split("@")[0];

  async function save() {
    setSaving(true);
    try {
      const res = await api.updateProfile(name);
      setName(res.display_name ?? "");
      setEditing(false);
    } catch {
      /* Biarkan form terbuka agar user bisa mencoba lagi. */
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-4 rounded-lg border border-border-strong bg-surface p-5
                    flex flex-wrap items-center gap-4">
      <div className="w-14 h-14 shrink-0 rounded-full bg-accent text-on-accent
                      flex items-center justify-center font-sans text-xl font-bold">
        {shown.charAt(0).toUpperCase()}
      </div>

      <div className="flex-1 min-w-[12rem]">
        {editing ? (
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              placeholder={email.split("@")[0]}
              className="flex-1 min-w-[10rem] rounded-sm border border-border bg-panel
                         px-3 py-1.5 font-sans text-sm text-ink-primary outline-none
                         focus:border-accent"
            />
            <Button variant="primary" onClick={save} loading={saving}>Simpan</Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>Batal</Button>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-sans text-lg text-ink-primary">{shown}</p>
            <button
              type="button"
              onClick={() => setEditing(true)}
              aria-label="Ubah nama"
              className="text-ink-dim hover:text-accent transition-colors"
            >
              <i className="fa-solid fa-pen-to-square text-xs" aria-hidden />
            </button>
          </div>
        )}

        <p className="mt-1 font-sans text-sm text-ink-muted flex items-center gap-1.5">
          <i className="fa-solid fa-envelope text-xs" aria-hidden /> {email}
        </p>
      </div>

      <div className="text-right">
        <span className="inline-flex items-center gap-1.5 rounded-sm border border-accent/40
                         bg-accent/10 px-2.5 py-1 font-sans text-xs text-accent">
          <i className={`fa-solid ${role === "owner" ? "fa-crown" : "fa-user"}`} aria-hidden />
          {role === "owner" ? "Pemilik toko" : "Pembeli"}
        </span>
        {joined && (
          <p className="mt-1.5 font-sans text-xs text-ink-dim">
            Bergabung {formatDate(joined)}
          </p>
        )}
      </div>
    </div>
  );
}

export function DashboardPage() {
  const { user, loading: authLoading } = useAuth();
  // Dipakai hanya untuk angka di tab; isinya dirender WishlistPanel.
  const { wishlist } = useCart();
  const [searchParams] = useSearchParams();
  const justOrdered = searchParams.get("just_ordered");

  const [vault, setVault] = useState<UserVaultResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("active");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getVault();
      setVault(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load vault");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user) load();
  }, [authLoading, user, load]);

  if (authLoading) return <LoadingState label="VERIFYING_SESSION" />;
  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <MonoText size="sm" color="ink-muted">[ AUTH_REQUIRED ]</MonoText>
        <h1 className="mt-4 font-sans text-2xl text-ink-primary">
          Vault access requires authentication
        </h1>
        <p className="mt-2 font-mono text-xs text-ink-muted">
          Sign in with Google to view your entitlements.
        </p>
        <Link to="/products" className="inline-block mt-6">
          <Button variant="primary">Browse Catalog →</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <h1 className="font-sans text-3xl text-ink-primary">Akun Saya</h1>

      {/* Kartu akun */}
      <AccountCard email={user.email} role={user.role} />

      {justOrdered && (
        <div className="mb-6 rounded-lg border border-border bg-panel p-4 flex items-center gap-3">
          <i className="fa-solid fa-hourglass-half text-accent" aria-hidden />
          <div className="flex-1">
            <p className="font-sans text-sm text-ink-primary">
              Pesanan dibuat. Produk akan muncul di sini otomatis setelah pembayaran masuk.
            </p>
            <p className="font-mono text-2xs text-ink-dim mt-0.5">{justOrdered}</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-3 mt-8 flex-wrap gap-3">
        <h2 className="font-sans text-xl text-ink-primary">Produk Saya</h2>
        <Button variant="ghost" onClick={load}>
          <i className="fa-solid fa-rotate mr-1.5" aria-hidden /> Muat ulang
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 border-b border-border">
        {(["active", "lifetime", "expired", "wishlist"] as const).map((t) => {
          const count =
            t === "active"
              ? vault?.active.length ?? 0
              : t === "lifetime"
                ? vault?.lifetime.length ?? 0
                : t === "expired"
                  ? vault?.expired.length ?? 0
                  : wishlist.length;
          return (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex items-center gap-2 font-sans text-sm px-4 py-2 border-b-2 -mb-px
                          transition-colors ${
                tab === t
                  ? "border-accent text-accent"
                  : "border-transparent text-ink-muted hover:text-ink-primary"
              }`}
            >
              <i className={`fa-solid ${TAB_ICONS[t]} text-xs`} aria-hidden />
              {TAB_LABELS[t]} ({count})
            </button>
          );
        })}
      </div>

      {/* Content */}
      {tab === "wishlist" ? (
        <WishlistPanel />
      ) : loading ? (
        <LoadingState label="MEMUAT_PRODUK" />
      ) : error ? (
        <ErrorState error={error} onRetry={load} />
      ) : !vault ? (
        <LoadingState />
      ) : (
        <EntitlementList
          items={
            tab === "active"
              ? vault.active
              : tab === "lifetime"
                ? vault.lifetime
                : vault.expired
          }
          tab={tab}
        />
      )}
    </div>
  );
}

function EntitlementList({ items, tab }: { items: VaultEntitlement[]; tab: Tab }) {
  if (items.length === 0) {
    return (
      <EmptyState>
        {tab === "active" || tab === "lifetime"
          ? "No active entitlements. Purchase something from the catalog to fill your vault."
          : "No expired items. Keep it that way."}
      </EmptyState>
    );
  }
  return (
    <div className="space-y-4">
      {items.map((e) => (
        <EntitlementCard key={e.entitlement_id} item={e} />
      ))}
    </div>
  );
}

// ============================================================
// Entitlement Card — render berdasarkan delivery_type
// ============================================================

function EntitlementCard({ item }: { item: VaultEntitlement }) {
  const isLifetime = item.is_lifetime;
  const isExpired = item.status === "EXPIRED";
  const isActive = item.status === "ACTIVE";

  // Ringkas jadi satu baris: dulu ini kotak tersendiri setinggi ~80px hanya
  // untuk menampilkan satu kalimat.
  const validity = isLifetime
    ? "Sekali Bayar · tanpa kedaluwarsa"
    : isExpired
      ? `Kedaluwarsa ${formatDate(item.expires_at)}`
      : item.remaining_days !== null
        ? `Sisa ${item.remaining_days} hari · sampai ${formatDate(item.expires_at)}`
        : `Berlaku sampai ${formatDate(item.expires_at)}`;

  return (
    <Panel padded={false} active={isActive}>
      <div className="p-4 sm:p-5">
        {/* Kepala: thumbnail persegi + identitas, sejajar horizontal */}
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 rounded-md overflow-hidden
                          border border-border bg-panel">
            {item.product_images[0] ? (
              <img
                src={item.product_images[0]}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              // Inisial kode produk jauh lebih berguna daripada teks "no_media".
              <div className="w-full h-full flex items-center justify-center bg-accent/10">
                <span className="font-mono text-base font-bold text-accent">
                  {item.product_code.slice(0, 3)}
                </span>
              </div>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge
                variant={isExpired ? "expired" : isActive || isLifetime ? "active" : "failed"}
              >
                {item.status}
              </Badge>
              <MonoText size="xs" color="ink-dim">
                {item.delivery_type.replace(/_/g, " ")}
              </MonoText>
            </div>

            <h3 className="mt-1.5 font-sans text-base sm:text-lg text-ink-primary leading-snug">
              {item.product_name}
            </h3>

            {/* Semua metadata jadi satu baris, bukan tiga blok terpisah. */}
            <p className="mt-1 font-mono text-2xs text-ink-dim">
              {item.product_code} · dibeli {formatDate(item.created_at)} · {validity}
            </p>
          </div>
        </div>

        {/* Isi yang dibeli */}
        {item.delivery_type === "SERIAL_KEY" && (
          <SecretRow label="Kode lisensi" value={item.issued_data} />
        )}
        {item.delivery_type === "ACCOUNT_CREDENTIAL" && (
          <SecretRow label="Akun" value={item.issued_data} copyLabel="SALIN AKUN" />
        )}
        {item.delivery_type === "STATIC_LINK_ONLY" && (
          <SecretRow label="Tautan produk" value={item.issued_data} href={item.issued_data} />
        )}

        {/* Link aplikasi — cukup satu baris, URL panjang tidak perlu dipajang.
            Untuk STATIC_LINK_ONLY sudah tampil di atas sebagai produknya. */}
        {item.delivery_type !== "STATIC_LINK_ONLY" && item.download_url && (
          <a
            href={item.download_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1.5 font-sans text-sm
                       text-ink-muted hover:text-accent transition-colors"
          >
            <span aria-hidden>↗</span> Unduh aplikasi / panduan
          </a>
        )}
      </div>
    </Panel>
  );
}

/**
 * Wishlist — sebelumnya tersimpan di localStorage tapi tidak pernah punya
 * tempat untuk dilihat, jadi tombol "Simpan ke Wishlist" terasa tidak berfungsi.
 */
function WishlistPanel() {
  const { wishlist, toggleWishlist } = useCart();

  if (wishlist.length === 0) {
    return (
      <Panel>
        <div className="text-center py-8">
          <i className="fa-regular fa-heart text-3xl text-ink-dim" aria-hidden />
          <p className="mt-3 font-sans text-sm text-ink-muted">
            Belum ada produk yang Anda simpan.
          </p>
          <Link to="/products" className="inline-block mt-4">
            <Button variant="secondary">Jelajahi Katalog</Button>
          </Link>
        </div>
      </Panel>
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {wishlist.map((w) => (
        <div
          key={w.product_slug}
          className="flex items-center gap-3 rounded-lg border border-border bg-surface p-3"
        >
          <div className="w-14 h-14 shrink-0 rounded-md overflow-hidden border border-border bg-panel">
            {w.image ? (
              <img src={w.image} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-accent/10">
                <i className="fa-solid fa-box text-accent" aria-hidden />
              </div>
            )}
          </div>
          <p className="flex-1 min-w-0 font-sans text-sm text-ink-primary truncate">
            {w.product_name}
          </p>
          <Link to={`/products?q=${encodeURIComponent(w.product_name)}`}>
            <Button variant="ghost">Lihat</Button>
          </Link>
          <button
            type="button"
            aria-label="Hapus dari wishlist"
            onClick={() => toggleWishlist(w)}
            className="w-8 h-8 text-ink-dim hover:text-hot transition-colors"
          >
            <i className="fa-solid fa-xmark" aria-hidden />
          </button>
        </div>
      ))}
    </div>
  );
}

/**
 * Satu baris rahasia: label, nilai, tombol salin. Dipakai ketiga jenis produk
 * supaya tingginya seragam.
 *
 * Baris "preview" yang dulu ada sengaja dibuang — isinya sama persis dengan
 * nilai di atasnya ketika payload tidak mengandung ":", jadi hanya menggandakan
 * teks yang sama dan menambah tinggi kartu tanpa memberi informasi.
 */
function SecretRow({
  label,
  value,
  copyLabel,
  href,
}: {
  label: string;
  value: string;
  copyLabel?: string;
  href?: string;
}) {
  return (
    <div className="mt-3 rounded-md border border-border bg-panel px-3 py-2.5">
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono text-2xs uppercase tracking-widest text-ink-dim shrink-0">
          {label}
        </span>
        <CopyButton value={value} label={copyLabel} />
      </div>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 block font-mono text-sm text-accent underline break-all hover:text-accent-soft"
        >
          {value}
        </a>
      ) : (
        <div className="mt-1 font-mono text-sm text-ink-primary break-all select-all">
          {value}
        </div>
      )}
    </div>
  );
}